import { useEffect, useState } from 'react';

import { addGlobalEventListener, removeGlobalEventListener } from '@luna/events';

import { stripHash } from './hash/hashUtils';
import { getCurrentRouterStrategy } from './routerTypeStrategy';

function getCurrentRoute<T extends string>(): T {
    return getCurrentRouterStrategy().getCurrentRoute() as T;
}

export function getCurrentUrlWithoutHash<T extends string>(): T {
    return stripHash(getCurrentRoute()) as T;
}

type HookOption = {
    removeHash: boolean;
};

/**
 * A hook that provides the current route and updates when the route changes.
 *
 * @template T The type of the route string
 * @param {HookOption} [option] Options to customize the hook behavior
 * @returns {T} The current route
 *
 * @example
 * ```tsx
 * import { useCurrentRoute } from '@luna/router';
 *
 * const RouteDisplay = () => {
 *   const currentRoute = useCurrentRoute();
 *   return <div>Current Route: {currentRoute}</div>;
 * };
 *
 * const RouteDisplayWithoutHash = () => {
 *   const currentRoute = useCurrentRoute({ removeHash: true });
 *   return <div>Current Route (without hash): {currentRoute}</div>;
 * };
 * ```
 */
export function useCurrentRoute<T extends string>(option?: HookOption): T {
    const getRoute = option?.removeHash ? getCurrentUrlWithoutHash<T> : getCurrentRoute<T>;

    const [currentRoute, setCurrentRoute] = useState<T>(getRoute());

    useEffect(() => {
        const handleRouteChange = (): void => setCurrentRoute(getRoute());

        addGlobalEventListener('route:change', handleRouteChange);

        return () => {
            removeGlobalEventListener('route:change', handleRouteChange);
        };
    }, []);

    return currentRoute;
}
